import React, { useEffect, useState } from 'react';
import {DeployServerInfo, ServiceInfo} from "../entities/ServiceInfo";
import {Button, Checkbox, Form, Input, InputNumber, Select, Table, Radio, Alert} from "antd";
import { DEPLOY_ENV } from "../utils/config";
import { DeployTemplate, DeployParam } from "../entities/DeployTemplate";
import { DeployTemplateService } from "../http/DeployTemplateService";
import { PackageTemplateService } from "../http/PackageTemplateService";
import { toast } from "react-toastify";
import PackageComponent from "../components/PackageComponent";
import { PackageTemplate, PackageParam } from "../entities/PackageTemplate";
import { PackageRecord } from "../entities/PackageRecord";
import DeployService, {DeployPackageProps} from "../http/DeployService";
import './DeployPage.css';
import PackageService from "../http/PackageService";
import TextArea from "antd/es/input/TextArea";
import {ServerInfoTable} from "../components/ServerInfoTable";

const { Option } = Select;

interface DeployPageProps {
    service: ServiceInfo;
}

const DeployPage: React.FC<DeployPageProps> = ({ service }) => {
    const [form] = Form.useForm();
    const [template, setTemplate] = useState<DeployTemplate | null>(null);
    const [packageTemplate, setPackageTemplate] = useState<PackageTemplate | null>(null);
    const [isNew, setIsNew] = useState<boolean>(true);
    const defaultDeployParams = DeployParam.ofDefault();
    const [params, setParams] = useState<DeployParam>(defaultDeployParams);
    const defaultPackageParams = PackageParam.ofDefault();
    const [packageParams, setPackageParams] = useState<PackageParam>(defaultPackageParams);
    const [usePackageComponent, setUsePackageComponent] = useState<boolean>(false);
    const [enableRemoteDebug, setEnableRemoteDebug] = useState<boolean>(defaultDeployParams.enableRemoteDebug);
    const [packages, setPackages] = useState<PackageRecord[]>([]);
    const [packageRecord, setPackageRecord] = useState<PackageRecord | null>(null);
    const [file, setFile] = React.useState<File | null>(null);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPackages, setTotalPackages] = useState(0);
    const [deployServers, setDeployServers] = useState<DeployServerInfo[]>([]);

    useEffect(() => {
        form.setFieldsValue({ "packageRecord": null});
        PackageService.listPackages({ appKey: service.appKey, env: DEPLOY_ENV, page: { pageNum: pageNum, pageSize: pageSize } }).then((response) => {
            setPackages(response.data.list || []);
            setTotalPackages(response.data.page.totalCount || 0);
        });
        PackageTemplateService.getTemplate({ appKey: service.appKey, env: DEPLOY_ENV }).then((response) => {
            setPackageTemplate(response.data);
            const param = response.data ? response.data!.param : defaultPackageParams;
            setPackageParams(param);
            form.setFieldsValue({ ...param });
        });
        DeployTemplateService.getTemplate({ appKey: service.appKey, env: DEPLOY_ENV }).then((response) => {
            setTemplate(response.data);
            const params = response.data ? response.data!.param : defaultDeployParams;
            setParams(params);
            form.setFieldsValue({ ...params });
            setIsNew(response.data == null);
        });
    }, [service.appKey, pageNum, pageSize]);

    useEffect(() => {
        DeployService.listDeployServers({ appKey: service.appKey, env: DEPLOY_ENV }).then((response) => {
            setDeployServers(response.data || []);
        });
    }, [service.appKey]);

    const deploy = async (gray: boolean) => {
        const values = await form.validateFields();
        const deployParam = { ...values, enableRemoteDebug: enableRemoteDebug };
        const record = { id: packageRecord?.id, appKey: service.appKey, env: DEPLOY_ENV, param: deployParam as PackageParam, packageName: values.packageName } as PackageRecord;
        const ips = deployServers.filter(server => selectedServerKeys.includes(server.internalIp)).map(server => server.internalIp);
        if (ips.length === 0){
            toast.error("请选择要部署的机器！");
            return;
        }
        const deployRequest = {
            packageRecord: record,
            deployParam: deployParam as DeployParam,
            ips: ips,
            releaseNote: values.releaseNote
        } as DeployPackageProps;
        await DeployService.deploy(deployRequest, gray).then((response) => {
            if (response.isFailed()) {
                return;
            }
            toast.success(`${gray ? "灰度" : ""}部署开始`);
            window.open(`/detail/deploy?id=${response.data.id}`, "_blank");
        });
    };

    const saveTemplate = async () => {
        try {
            const params = await form.validateFields() as DeployParam;
            const newTemplate = { ...template, appKey: service.appKey, env: DEPLOY_ENV, param: params } as DeployTemplate;
            if (isNew) {
                DeployTemplateService.createTemplate(newTemplate).then((response) => {
                    if (response.isFailed()) {
                        return;
                    }
                    toast.success("模板创建成功！");
                    setIsNew(false);
                    setTemplate(response.data);
                });
            } else {
                DeployTemplateService.updateTemplate(newTemplate).then((response) => {
                    if (response.isFailed()) {
                        return;
                    }
                    toast.success("模板更新成功！");
                });
            }
        } catch (error) {
            console.error("Form validation failed", error);
        }
    };

    const handlePageChange = (page: number, size?: number) => {
        setPageNum(page);
        if (size) {
            setPageSize(size);
        }
    };

    const searchPackages = (value: string) => {
        PackageService.listPackages({ appKey: service.appKey, env: DEPLOY_ENV, keyword: value, page: { pageNum: 1, pageSize: pageSize } }).then((response) => {
            setPackages(response.data.list || []);
            setTotalPackages(response.data.page.totalCount || 0);
        });
    };

    const [selectedServerKeys, setSelectedServerKeys] = useState<React.Key[]>([]);
    const rowSelection = {
        selectedRowKeys: selectedServerKeys,
        onChange: (selectedRowKeys: React.Key[]) => {
            setSelectedServerKeys(selectedRowKeys);
        },
    };

    return (
        <div className="deploy-page">
            {service.currentDeployJobs && service.currentDeployJobs.length > 0 && (
                <Alert
                    className="deploy-page-alert"
                    message="当前该服务正在部署中，禁止重复部署"
                    description={
                        <Button type="link" onClick={() => window.open(`/detail/deploy?id=${service.currentDeployJobs![0].id}`)}>
                            查看部署任务
                        </Button>
                    }
                    type="warning"
                    showIcon
                />
            )}
            <Form
                form={form}
                layout="horizontal"
                labelCol={{ style: { width: "150px" } }}
                initialValues={params}>
                <p className="section-header">构建配置</p>
                <Form.Item label="基于包部署">
                    <Radio.Group
                        value={usePackageComponent}
                        onChange={(e) => setUsePackageComponent(e.target.value)}
                    >
                        <Radio value={false}>是</Radio>
                        <Radio value={true}>否</Radio>
                    </Radio.Group>
                </Form.Item>
                {usePackageComponent ? (
                    <PackageComponent
                        service={service}
                        form={form}
                        templateLoaded={setPackageTemplate}
                        filePicked={setFile}
                    />
                ) : (
                    <Form.Item label="构建包" name="packageRecord" required={true}>
                        <Select
                            showSearch
                            placeholder="请选择构建包"
                            onSearch={searchPackages}
                            onSelect={(value) => {
                                const record = packages.find((pkg) => pkg.id === value);
                                setPackageRecord(record || null);
                            }}
                        >
                            {packages.map((pkg) => (
                                <Option key={pkg.id} value={pkg.id}>{pkg.packageName}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
                <p className="section-header">部署配置</p>
                <Form.Item label="检测端口" name="port" required={true}>
                    <InputNumber placeholder="8080" />
                </Form.Item>
                <Form.Item label="检查路径" name="checkPath" required={true}>
                    <Input placeholder="如/aliveStatus" />
                </Form.Item>
                <Form.Item label="最长检查时间（秒）" name="maxCheckSeconds">
                    <InputNumber placeholder="120" />
                </Form.Item>
                <Form.Item label="检查间隔（秒）" name="checkInterval">
                    <InputNumber placeholder="5" />
                </Form.Item>
                <Form.Item label="部署并发度" name="concurrency" required={true}>
                    <InputNumber placeholder="部署数量" />
                </Form.Item>
                <Form.Item label="失败时停止" name="stopWhenFailed" valuePropName="checked" required={true}>
                    <Checkbox>开启</Checkbox>
                </Form.Item>
                {DEPLOY_ENV !== 'prod' && service.type === 'SPRINGBOOT' && (
                    <>
                        <Form.Item label="是否开启远程debug" name="enableRemoteDebug" valuePropName="checked">
                            <Checkbox onChange={(e) => {
                                const checked = e.target.checked;
                                setEnableRemoteDebug(checked);
                                if (checked) {
                                    form.setFieldsValue({ debugPort: defaultDeployParams.debugPort });
                                }
                            }}>开启远程debug</Checkbox>
                        </Form.Item>
                        {
                            enableRemoteDebug && <Form.Item label="远程debug端口" name="debugPort">
                                <InputNumber placeholder="8418" />
                            </Form.Item>
                        }
                    </>
                )}
                {
                    service.type === 'SPRINGBOOT' && (
                        <Form.Item label="自定义JVM参数" name="customJvmArgs" required={false}>
                            <Input placeholder="比如-Xms256m -Xmx512m" />
                        </Form.Item>
                    )
                }
                <Form.Item label="发布备注" name="releaseNote">
                    <TextArea/>
                </Form.Item>
            </Form>
            <ServerInfoTable servers={deployServers} onServerSelected={setSelectedServerKeys}/>
            <div className="fixed-footer">
                <Button type="default" onClick={saveTemplate}>{isNew ? "保存模板" : "更新模板"}</Button>
                <Button type="default" onClick={() => deploy(false)} disabled={service.currentDeployJobs != null && service.currentDeployJobs.length > 0}>
                    部署
                </Button>
                <Button type="primary" onClick={() => deploy(true)} disabled={service.currentDeployJobs != null && service.currentDeployJobs.length > 0}>
                    灰度部署
                </Button>
            </div>
        </div>
    );
};

export default DeployPage;